import React, { useMemo, useState } from "react";
import { SHOWCASE_GROUPS, componentRegistry } from "./componentRegistry";
import "./showcase.css";

// Collapsible "Show code" panel (hidden by default), MUI-style.
const CodePanel = ({ code }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="gx-code">
      <div className="gx-code-bar">
        <button className="gx-code-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? "Hide code" : "Show code"}
        </button>
        {open && (
          <button className="gx-code-copy" onClick={copy}>{copied ? "Copied!" : "Copy"}</button>
        )}
      </div>
      {open && (
        <pre className="gx-code-block">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
};

/**
 * /design/components — the interactive catalog for the shared component
 * library. Renders straight from componentRegistry; primitives are imported
 * there, not here. Public, no app chrome. See
 * docs/specs/manager-dashboard-component-audit.md.
 */

const PropsTable = ({ props }) => {
  if (!props || props.length === 0) return null;
  return (
    <table className="gx-props">
      <thead>
        <tr><th>Prop</th><th>Type</th><th>Default</th><th>Description</th></tr>
      </thead>
      <tbody>
        {props.map((p) => (
          <tr key={p.name}>
            <td><code>{p.name}</code></td>
            <td><code className="gx-type">{p.type}</code></td>
            <td>{p.default ? <code>{p.default}</code> : <span className="gx-dim">—</span>}</td>
            <td>{p.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ComponentBlock = ({ entry }) => {
  const planned = entry.status !== "live";
  return (
    <article id={entry.id} className={`gx-block ${planned ? "is-planned" : ""}`}>
      <header className="gx-block-head">
        <h3>{entry.name}</h3>
        <span className={`gx-status gx-status--${planned ? "planned" : "live"}`}>
          {planned ? "Planned" : "Live"}
        </span>
      </header>
      <p className="gx-desc">{entry.description}</p>

      {entry.variants && entry.variants.length > 0 && (
        <div className="gx-variants">
          {entry.variants.map((v) => (
            <div key={v.label} className="gx-variant">
              <div className="gx-variant-stage">{v.render()}</div>
              <div className="gx-variant-label">{v.label}</div>
            </div>
          ))}
        </div>
      )}

      {entry.code && <CodePanel code={entry.code} />}

      {planned && (
        <div className="gx-planned-note">
          Audited, not yet built — see the component audit (Lists A + B).
        </div>
      )}

      <PropsTable props={entry.props} />

      {entry.notes && entry.notes.length > 0 && (
        <ul className="gx-notes">
          {entry.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </article>
  );
};

const ComponentShowcase = () => {
  const [query, setQuery] = useState("");

  const byGroup = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (e) =>
      !q || e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q);
    const map = {};
    SHOWCASE_GROUPS.forEach((g) => { map[g] = []; });
    componentRegistry.filter(matches).forEach((e) => {
      (map[e.group] || (map[e.group] = [])).push(e);
    });
    return map;
  }, [query]);

  const liveCount = componentRegistry.filter((e) => e.status === "live").length;

  return (
    <div className="gx-showcase">
      <header className="gx-top">
        <div className="gx-top-inner">
          <div className="gx-wordmark">GROUPARDY</div>
          <span className="gx-top-sub">Component Library</span>
          <input
            className="gx-search"
            placeholder="Filter components…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="gx-top-meta">
          {liveCount} live · {componentRegistry.length - liveCount} planned ·
          {" "}brand tokens from <code>public/design/tokens.css</code>
        </div>
      </header>

      <nav className="gx-nav">
        {SHOWCASE_GROUPS.map((g) => (
          <a key={g} href={`#group-${g.replace(/\W+/g, "-").toLowerCase()}`}>{g}</a>
        ))}
      </nav>

      <main className="gx-main">
        {SHOWCASE_GROUPS.map((group) => {
          const entries = byGroup[group] || [];
          return (
            <section
              key={group}
              id={`group-${group.replace(/\W+/g, "-").toLowerCase()}`}
              className="gx-group"
            >
              <h2 className="gx-group-title">{group}</h2>
              {entries.length === 0 ? (
                <div className="gx-empty">No components{query ? " match" : " here yet"}.</div>
              ) : (
                entries.map((e) => <ComponentBlock key={e.id} entry={e} />)
              )}
            </section>
          );
        })}
      </main>
    </div>
  );
};

export default ComponentShowcase;
